/**
 * The one range a page's charts share (round 1, dataviz M5): 30 or 90 days by day, 12 or 24 months
 * by week, held in the URL as `?range=`. The window says how much to read so the previous span can
 * be drawn as a ghost, and the buckets turn days into the columns the range draws. Pure.
 *
 * The ghost compares whole weeks (round 2, dataviz N1): the same weekdays 4 weeks earlier for 30
 * days, 13 for 90 days, and 52 for the weekly ranges, so last period's weekdays never land on this
 * period's weekends.
 */
import type { VolumeDay } from '../types';

export const ranges = ['30d', '90d', '12m', '24m'] as const;
export type Range = (typeof ranges)[number];

export const rangeLabel: Record<Range, string> = {
  '30d': '30 days',
  '90d': '90 days',
  '12m': '12 months',
  '24m': '24 months',
};

/** The days a range covers. */
export const rangeDays: Record<Range, number> = { '30d': 30, '90d': 90, '12m': 364, '24m': 728 };

/** How many whole weeks back the ghost looks: 28 days for 30, 91 for 90, 52 weeks for the weekly ranges. */
export const ghostWeeks: Record<Range, number> = { '30d': 4, '90d': 13, '12m': 52, '24m': 52 };

/** The days a daily ghost averages over. */
const AVERAGE = 7;

/** What to read for a range: the span, the ghost's shift and its average (the API's cap is 731). */
export function windowOf(range: Range): { days: number; hourDays: number; months: number } {
  return {
    days: Math.min(731, rangeDays[range] + ghostWeeks[range] * 7 + AVERAGE - 1),
    hourDays: rangeDays[range],
    months: range === '24m' ? 24 : 12,
  };
}

/** One column of the pages chart: a day, or a week ending on its last day. */
export interface Bucket {
  first: string;
  last: string;
  pages: number;
  documents: number;
}

/** The same column whole weeks earlier: its exact pages and its days, for the tooltip. */
export interface Earlier {
  first: string;
  last: string;
  pages: number;
}

export interface Buckets {
  now: Bucket[];
  /** The same column whole weeks earlier, undefined where there is no history to compare with. */
  earlier: (Earlier | undefined)[];
  /** The ghost line: the earlier days' 7-day average, or the earlier week's pages; undefined where there is nothing. */
  ghost: (number | undefined)[];
  /** How many weeks back the ghost looks. */
  weeks: number;
}

function bucketsBySize(days: readonly VolumeDay[], size: number): Bucket[] {
  const all: Bucket[] = [];
  for (let end = days.length; end - size >= 0; end -= size) {
    const slice = days.slice(end - size, end);
    all.unshift({
      first: slice[0]!.day,
      last: slice.at(-1)!.day,
      pages: slice.reduce((s, d) => s + d.pages, 0),
      documents: slice.reduce((s, d) => s + d.documents, 0),
    });
  }
  return all;
}

/**
 * The mean of the `AVERAGE` buckets ending at `at`, or undefined unless they were all read and all
 * fall after the first pages: an average over days before there was history would ramp up from zero.
 */
function averageAt(all: readonly Bucket[], at: number, historyFrom: string): number | undefined {
  const first = all[at - AVERAGE + 1];
  if (first === undefined || first.first < historyFrom) return undefined;
  const slice = all.slice(at - AVERAGE + 1, at + 1);
  return slice.reduce((s, b) => s + b.pages, 0) / AVERAGE;
}

/** The column `shift` back from `at`, with its ghost value, when it was read and had history. */
function backOf(
  all: readonly Bucket[],
  at: number,
  shift: number,
  look: { daily: boolean; historyFrom: string | null },
): { earlier: Earlier | undefined; ghost: number | undefined } {
  const back = all[at - shift];
  if (back === undefined || look.historyFrom === null || back.last < look.historyFrom)
    return { earlier: undefined, ghost: undefined };
  return {
    earlier: { first: back.first, last: back.last, pages: back.pages },
    ghost: look.daily ? averageAt(all, at - shift, look.historyFrom) : back.pages,
  };
}

/**
 * The range's columns, oldest first, and the same columns whole weeks earlier where the days reach
 * and the company already had pages (`historyFrom`, yyyy-MM-dd; null for none ever).
 */
export function bucketsOf(
  days: readonly VolumeDay[],
  range: Range,
  historyFrom: string | null = null,
): Buckets {
  const daily = range.endsWith('d');
  const all = bucketsBySize(days, daily ? 1 : 7);
  const count = Math.min(all.length, rangeDays[range] / (daily ? 1 : 7));
  const shift = daily ? ghostWeeks[range] * 7 : ghostWeeks[range];
  const start = all.length - count;
  const backs = all
    .slice(start)
    .map((_, i) => backOf(all, start + i, shift, { daily, historyFrom }));
  return {
    now: all.slice(start),
    earlier: backs.map((x) => x.earlier),
    ghost: backs.map((x) => x.ghost),
    weeks: ghostWeeks[range],
  };
}

/** The first column with something to compare with, or -1 when there is none. */
export function comparedFrom(b: Buckets): number {
  return b.ghost.findIndex((g) => g !== undefined);
}

export function isRange(value: unknown): value is Range {
  return typeof value === 'string' && (ranges as readonly string[]).includes(value);
}
